import { gql } from '@apollo/client'
import queryString from 'query-string'
import equals from 'ramda/src/equals'
import flatten from 'ramda/src/flatten'

import { PROVIDERS } from 'consts'
import { client } from 'lib'
import UserStore from 'stores/UserStore'

import datadog from './datadog'
import { GLOBAL_TAXONOMY_PATHNAMES_BY_TYPE, GLOBAL_TAXONOMY_TYPES_BY_TYPENAME } from './territoryUtils/consts'
import defaultFilterValues from '../consts/filterDefaults'
import groups from '../consts/filterGroups'

const TOPICS_QUERY = gql`
  query TOPICS_QUERY($topicIds: [Int!]!) {
    topics(topicIds: $topicIds) {
      title
      topicId
      language
      createdAt
      category {
        topicGroupId
        title
      }
    }
  }
`

const SUBTOPICS_QUERY = gql`
  query FILTER_UTIL_SUBTOPICS_QUERY($subtopicIds: [Int!]!) {
    getSubtopics(subtopicIds: $subtopicIds) {
      title
      subtopicId
      language
      topic {
        createdAt
        title
        topicId
      }
    }
  }
`

const CREATORS_QUERY = gql`
  query CREATORS_QUERY($creatorsInfo: [ExternalEntityInput!]!) {
    creators(creatorsInfo: $creatorsInfo) {
      ... on Creator {
        creatorId
        provider
        title
        isBrand
        isTrending
      }
    }
  }
`

const CATEGORY_QUERY = gql`
  query topicGroupById($topicGroupId: Int!) {
    topicGroupById(topicGroupId: $topicGroupId) {
      title
      language
      topics {
        createdAt
        topicId
      }
    }
  }
`

const SUBTOPIC_QUERY = gql`
  query SUBTOPIC($id: Int!) {
    subtopic(id: $id) {
      name
      subtopicId
      language
      topic {
        createdAt
        topicId
      }
    }
  }
`

const SAVED_POST_SEARCHES_QUERY = gql`
  query SAVED_POST_SEARCHES_QUERY($ids: [ID!]!) {
    getSavedPostSearches(ids: $ids) {
      id
      name
      query
    }
  }
`

const LIST_TERRITORIES = gql`
  query LIST_TERRITORIES($ids: [ID!]!) {
    listTerritories(ids: $ids) {
      territoryId
      name
      ... on Niche {
        id
        parent {
          territoryId
          name
        }
      }
    }
  }
`

const TERRITORY_QUERY = gql`
  query TERRITORY_QUERY($territoryId: ID!) {
    getTerritory(territoryId: $territoryId) {
      name
      territoryId
      ... on SavedPostSearch {
        query
      }
      ... on Subtopic {
        language
        topic {
          topicId
          title
        }
      }
      ... on Topic {
        language
        category {
          title
          topicGroupId
        }
      }
      ... on Niche {
        parent {
          territoryId
          name
        }
      }
    }
  }
`

class Filters {
  list = {
    ...groups,
  }

  async getTerritoryById(territoryId) {
    const query = TERRITORY_QUERY
    return client
      .query({
        query,
        variables: {
          territoryId,
        },
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        const territoryData = data?.getTerritory
        const baseId = territoryData?.territoryId?.split('.')?.[1]
        if (territoryData?.__typename === 'IndustryTopic') {
          return {
            value: baseId,
            label: territoryData.name,
          }
        }
        if (
          territoryData?.__typename === 'GlobalTopic' ||
          territoryData?.__typename in GLOBAL_TAXONOMY_TYPES_BY_TYPENAME
        ) {
          return {
            value: baseId,
            label: territoryData.name,
          }
        }
        if (territoryData?.__typename === 'Topic') {
          return {
            value: baseId,
            label: territoryData.name,
            type: 'topic',
            language: territoryData.language,
            groupId: territoryData?.category?.topicGroupId,
            category: territoryData?.category?.title,
          }
        }
        if (territoryData?.__typename === 'SavedPostSearch') {
          return {
            value: baseId,
            label: territoryData?.name,
            query: territoryData?.query,
            type: territoryData?.__typename,
          }
        }
        if (territoryData?.__typename === 'Subtopic') {
          return {
            value: baseId,
            label: territoryData.name,
            type: 'subtopic',
            topic: territoryData.topic.title,
            topicId: territoryData.topic.topicId,
            language: territoryData.language,
          }
        }
        if (territoryData?.__typename === 'Niche') {
          return {
            value: baseId,
            label: territoryData.name,
            type: 'niche',
            parent: territoryData.parent,
          }
        }
        if (territoryData?.__typename === 'Trending') {
          return {
            value: baseId,
            label: territoryData.name,
            type: 'trending',
          }
        }
        if (territoryData?.__typename === 'TerritoryTag') {
          return {
            value: baseId,
            label: territoryData.name,
            type: 'tag',
          }
        }
        return data
      })
  }

  getTopicById(topicIds) {
    const query = TOPICS_QUERY
    let params = topicIds
    if (typeof params === 'string' || typeof params === 'number') {
      params = [Number(topicIds)]
    }
    if (Array.isArray(topicIds)) {
      params = topicIds.map(id => Number(id))
    }
    const variables = {
      topicIds: params,
    }
    return client
      .query({
        query,
        variables,
      })
      .then(({ data }) => {
        const orderedTopics = Array.isArray(topicIds) ? [] : false
        if (data && orderedTopics) {
          topicIds.map(topicId =>
            data.topics.map(topic => {
              if (Number(topicId) === Number(topic.topicId)) orderedTopics.push(topic)
              return false
            })
          )
        }
        return (orderedTopics || data.topics).map(topic => {
          if (topic?.topicId) {
            return {
              value: topic.topicId,
              label: topic.title,
              type: 'topic',
              language: topic.language,
              groupId: topic?.category?.topicGroupId,
              category: topic?.category?.title,
            }
          }
          return {}
        })
      })
      .catch(e => console.error('getTopicById error >>>>>>>>>>>', e))
  }

  getSubtopicById(subtopicIds) {
    const query = SUBTOPICS_QUERY
    let params = subtopicIds
    if (typeof params === 'string' || typeof params === 'number') {
      params = [Number(subtopicIds)]
    }
    if (Array.isArray(subtopicIds)) {
      params = subtopicIds.map(id => Number(id))
    }
    const variables = {
      subtopicIds: params,
    }
    return client
      .query({
        query,
        variables,
      })
      .then(({ data }) => {
        const orderedSubtopics = Array.isArray(subtopicIds) ? [] : false
        if (data && orderedSubtopics) {
          subtopicIds.map(subtopicId =>
            data.getSubtopics.map(subtopic => {
              if (Number(subtopicId) === Number(subtopic.subtopicId)) orderedSubtopics.push(subtopic)
              return false
            })
          )
        }
        return (orderedSubtopics || data.getSubtopics).map(subtopic => {
          if (subtopic?.subtopicId) {
            return {
              value: subtopic.subtopicId,
              label: subtopic.title,
              type: 'subtopic',
              topic: subtopic.topic.title,
              topicId: subtopic.topic.topicId,
              language: subtopic.language,
            }
          }
          return {}
        })
      })
      .catch(e => console.error('getSubtopicById error >>>>>>>>>>>', e))
  }

  async getSavedPostSearchById(savedPostSearchIds) {
    const ids = Array.isArray(savedPostSearchIds) ? savedPostSearchIds : [savedPostSearchIds]

    const savedSearchData = await client
      .query({
        query: SAVED_POST_SEARCHES_QUERY,
        variables: { ids },
      })
      .then(({ data }) => {
        const savedPostSearches = data?.getSavedPostSearches ?? []

        const sortedSavedPostSearches = ids?.map(id => {
          const current = savedPostSearches?.find(savedPostSearch => savedPostSearch?.id === id)

          return {
            value: current?.id,
            label: current?.name,
            query: current?.query,
            type: current?.__typename,
          }
        })

        return sortedSavedPostSearches
      })
      .catch(e => {
        console.error('getSavedPostSearchesById @ query >>>>>>>>>>>', e)

        return null
      })

    const cleanData = savedSearchData.filter(item => item.value)

    return cleanData
  }

  async getIndustryTopicsById(territoryIds) {
    const ids = Array.isArray(territoryIds) ? territoryIds : [territoryIds]
    const idsWithPrefix = ids.map(id => {
      return id.includes('industry_topic.') ? id : `industry_topic.${id}`
    })
    const industry_topics = await client
      .query({
        query: LIST_TERRITORIES,
        variables: { ids: idsWithPrefix },
      })
      .then(({ data }) => {
        const industry_topic = data?.listTerritories ?? []

        const sorted_industry_topics = ids?.map(id => {
          const territoryIdToMatch = id.includes('industry_topic.') ? id : `industry_topic.${id}`
          const current = industry_topic?.find(territory => {
            return territory?.territoryId === territoryIdToMatch
          })

          return {
            value: id,
            label: current?.name,
            type: 'industry_topic',
            parent: current?.parent,
          }
        })

        return sorted_industry_topics
      })
      .catch(e => {
        datadog.dispatchRequestErrorLog({
          error: e,
          log: {
            from: 'utils/filters/getIndustryTopicsById',
            message: 'Error on getIndustryTopicsById',
            name: 'getIndustryTopicsById',
          },
          query: LIST_TERRITORIES,
        })

        return null
      })

    return industry_topics
  }

  async getTerritoriesByTypeAndIds(type, territoryIds) {
    const ids = Array.isArray(territoryIds) ? territoryIds : [territoryIds]
    const prefix = `${type}.`
    const getIdWithPrefix = id => (id.startsWith(prefix) ? id : `${prefix}${id}`)

    return client
      .query({
        query: LIST_TERRITORIES,
        variables: { ids: ids.map(getIdWithPrefix) },
      })
      .then(({ data }) => {
        const territories = data?.listTerritories ?? []

        return ids.map(id => {
          const current = territories.find(territory => territory?.territoryId === getIdWithPrefix(id))

          return {
            value: id,
            label: current?.name,
            type,
            parent: current?.parent,
          }
        })
      })
      .catch(e => {
        datadog.dispatchRequestErrorLog({
          error: e,
          log: {
            from: 'utils/filters/getTerritoriesByTypeAndIds',
            message: `Error on getTerritoriesByTypeAndIds for ${type}`,
            name: 'getTerritoriesByTypeAndIds',
          },
          query: LIST_TERRITORIES,
        })

        return null
      })
  }

  async getNichesByIDs(nicheIds) {
    const ids = Array.isArray(nicheIds) ? nicheIds : [nicheIds]
    const idsWithPrefix = ids.map(id => `niche.${id}`)

    const niches = await client
      .query({
        query: LIST_TERRITORIES,
        variables: { ids: idsWithPrefix },
      })
      .then(({ data }) => {
        const niches = data?.listTerritories ?? []

        const sortedNiches = ids?.map(id => {
          const current = niches?.find(niche => niche?.id === id)

          return {
            value: current?.id,
            label: current?.name,
            type: 'niche',
            parent: current?.parent,
          }
        })

        return sortedNiches
      })
      .catch(e => {
        console.error('getNichesByIDs', e)

        return null
      })

    return niches
  }

  async getTagsById(tagIds) {
    const ids = Array.isArray(tagIds) ? tagIds : [tagIds]
    const idsWithPrefix = ids.map(id => `tag.${id}`)

    const tags = await client
      .query({
        query: LIST_TERRITORIES,
        variables: { ids: idsWithPrefix },
      })
      .then(({ data }) => {
        const territories = data?.listTerritories ?? []
        const tags = territories.filter(territory => territory?.__typename === 'TerritoryTag')

        const sortedTags = ids?.map(id => {
          const current = tags?.find(tag => {
            const tagId = tag?.territoryId?.split('.')?.[1]
            return tagId === String(id)
          })

          return {
            value: current ? current?.territoryId?.split('.')?.[1] : id,
            label: current?.name,
            type: 'tag',
          }
        })

        return sortedTags
      })
      .catch(e => {
        console.error(`getTagsById failed for tagIds: [${ids.join(', ')}]. Error:`, e)
        return null
      })

    return tags
  }

  creatorQueryStringToFilter = qs => {
    if (qs && typeof qs === 'string') {
      const [provider, id] = qs.split(':')
      return {
        provider,
        id,
      }
    }
    return {}
  }

  async getCreators(creators) {
    const query = CREATORS_QUERY
    let params = creators
    if (typeof params === 'string' || typeof params === 'number') {
      params = [params]
    }
    params = params.map(qs => this.creatorQueryStringToFilter(qs))
    const promises = params.map(creatorsInfo => {
      const userHasAccess = UserStore.providers.includes(creatorsInfo?.provider)
      if (!userHasAccess) {
        return {}
      }
      return client
        .query({
          query,
          variables: { creatorsInfo },
        })
        .then(({ data }) => {
          const { creators } = data
          if (creators?.[0]) {
            const { creatorId, title: label, provider, isTrending, isBrand } = creators[0]
            return {
              value: `${provider}:${creatorId}`,
              label,
              provider,
              id: creatorId,
              isBrand,
              isTrending,
            }
          }
          return {}
        })
        .catch(e => console.error('error getting creators >>>>>>>>>>>', e))
    })
    return flatten(await Promise.all(promises)) || []
  }

  async getSubtopic(value) {
    const query = SUBTOPIC_QUERY
    const [, id] = value.split(':')
    const variables = { id: Number(id) }
    try {
      const { data } = await client.query({ query, variables })
      if (data.subtopic) {
        return {
          value: `${data.subtopic.topic.topicId}:${data.subtopic.subtopicId}`,
          label: data.subtopic.name,
          language: data.subtopic.language,
          topicId: data.subtopic.topic.topicId,
        }
      }
      return {}
    } catch (e) {
      console.error('error getting subtopic value >>>>>>>>>>>', e)
    }
  }

  async getTopicGroupTitle(topicGroupId) {
    const query = CATEGORY_QUERY
    const variables = { topicGroupId }

    try {
      const { data } = await client.query({ query, variables })
      if (data) {
        return {
          label: data.topicGroupById.title,
          value: topicGroupId,
          language: data?.topicGroupById?.language,
        }
      }
      return {
        value: topicGroupId,
        label: '',
      }
    } catch (e) {
      console.error('error getting topic group title >>>>>>>>>>>', e)
      return {
        value: topicGroupId,
        label: '',
      }
    }
  }
  async getCategoryOptionWithTopicIds(topicGroupId) {
    const query = CATEGORY_QUERY
    const variables = { topicGroupId }

    try {
      const { data, errors } = await client.query({ query, variables })
      if (errors) {
        console.error('error getting topic group topicIds >>>>>>>>>>>', errors)
        return {
          label: 'All Territories',
          value: '',
          errors,
          topicIds: [],
        }
      }
      if (data) {
        return {
          label: data.topicGroupById.title,
          value: topicGroupId,
          topicIds: data?.topicGroupById?.topics?.map(topic => topic.topicId),
          language: data?.topicGroupById?.language,
        }
      }
      return {
        value: topicGroupId,
        label: '',
        topicIds: [],
      }
    } catch (e) {
      console.error('error getting topic group topicIds >>>>>>>>>>>', e)
      return {
        value: topicGroupId,
        label: '',
        topicIds: [],
      }
    }
  }

  getCreatorTypeQuery(creatorType) {
    switch (creatorType.value) {
      case 'allTrending':
        return { isTrending: true, onlyBrands: null }
      case 'creators':
        return { isTrending: null, onlyBrands: false }
      case 'brands':
        return { isTrending: null, onlyBrands: true }
      case 'trendingCreators':
        return { isTrending: true, onlyBrands: false }
      case 'trendingBrands':
        return { isTrending: true, onlyBrands: true }
      default:
        return { isTrending: null, onlyBrands: null }
    }
  }

  getDefaultValuesFromGroup(group) {
    const filtersKeysInDefault = defaultFilterValues[group] && Object.keys(defaultFilterValues[group])

    if (!!filtersKeysInDefault?.length && filtersKeysInDefault?.includes('providers')) {
      const providersList = UserStore.providers.map(item => {
        return {
          value: item,
          text: PROVIDERS[item].NAME,
          label: PROVIDERS[item].NAME,
          color: PROVIDERS[item].COLOR,
        }
      })
      return {
        ...defaultFilterValues[group],
        providers: providersList,
      }
    }

    return defaultFilterValues[group] || {}
  }

  getDefaultFilter({ group, filter }) {
    if (defaultFilterValues[group]) {
      if (filter === 'providers') {
        const providersList = UserStore.providers.map(item => {
          return {
            value: item,
            text: PROVIDERS[item].NAME,
            label: PROVIDERS[item].NAME,
            color: PROVIDERS[item].COLOR,
          }
        })
        return providersList
      }
      return defaultFilterValues[group][filter] || {}
    }
    return {}
  }

  getActiveFilters(group, filters) {
    const defaultFilters = this.getDefaultValuesFromGroup(group)

    return Object.keys(this.getDefaultValuesFromGroup(group)).filter(filterName => {
      if (filterName === 'providers') {
        return filters[filterName].length !== defaultFilters[filterName].length
      }
      return filters[filterName].value !== defaultFilters[filterName].value
    })
  }

  get(group, filter, value = null, defaultField = false) {
    if (!group) return new Error('no group provided')

    const filterTitle = filter === 'title' || filter === 'titleAnd' || filter === 'titleOr' || filter === 'titleNot'

    if (filterTitle && typeof value === 'string') {
      return {
        value,
        label: value,
      }
    }

    if (filter === 'topicCategory' || filter === 'categories') {
      if ((value.value || value.value === '') && value.label) {
        return {
          label: value.label,
          value: value.value,
        }
      }
      return this.getTopicGroupTitle(Number(value))
        .then(res => res)
        .catch(e => console.error('get filter error >>>>>>>>>>>', e))
    }

    if (filter === 'subtopicCategory') {
      if ((value.value || value.value === '') && value.label && value.topicIds) {
        return {
          label: value.label,
          value: value.value,
          topicIds: value.topicIds,
        }
      }
      return this.getCategoryOptionWithTopicIds(Number(value))
        .then(res => {
          return res
        })
        .catch(e => console.error('get filter error >>>>>>>>>>>', e))
    }

    if (filter === 'subtopicTopic') {
      if ((value.value || value.value === '') && value.label) {
        return {
          label: value.label,
          value: value.value,
        }
      }
      return this.getTopicById(value)
        .then(res => res[0])
        .catch(e => console.error('get filter error >>>>>>>>>>>', e))
    }

    if (filter === 'topics' || filter === 'topicIds' || filter === 'musicGenre') {
      return this.getTopicById(value)
        .then(res => res)
        .catch(e => console.error('get filter error >>>>>>>>>>>', e))
    }

    if (filter === 'formatIds') {
      return {
        value: value,
      }
    }

    if (filter === 'subtopics' || filter === 'subtopicIds') {
      return this.getSubtopicById(value)
        .then(res => res)
        .catch(e => console.error('get filter error >>>>>>>>>>>', e))
    }

    if (filter === 'creators' || filter === 'brands') {
      return this.getCreators(value)
        .then(filters => filters)
        .catch(e => console.error('get creators error >>>>>>>>>>>', e))
    }

    if (filter === 'subtopic' || filter === 'musicSubgenre') {
      return this.getSubtopic(value)
        .then(filters => filters)
        .catch(e => console.error('get subtopic error >>>>>>>>>>>', e))
    }

    if (filter === 'providers') {
      const userProviders = UserStore.providers
      const filterValues = Array.isArray(value) ? value : [value]

      const filteredProviders = this.list[group][filter].filter(
        item => filterValues.includes(item.value) && userProviders.includes(item.value)
      )
      if (filteredProviders?.length > 0) {
        return filteredProviders
      }

      const allUserProviders = this.list[group][filter].filter(item => userProviders.includes(item.value))

      return allUserProviders
    }

    if (filter === 'tagTitle') {
      return {
        value,
      }
    }

    const isTopicPeopleAlsoWatchTab =
      group === 'topicPeopleAlsoWatchTab' ||
      group === 'topicPage' ||
      group === 'topicOverviewTab' ||
      group === 'trendingVideos'

    if (filter === 'alsoWatchCategory' && isTopicPeopleAlsoWatchTab) {
      return [
        {
          value: value,
          key: value,
        },
      ]
    }

    if (filter === 'savedPostSearches') {
      return this.getSavedPostSearchById(value)
        .then(filters => filters)
        .catch(e =>
          datadog.dispatchGenericErrorLog({
            error: e,
            log: {
              from: 'utils/filters',
              message: 'Error on filters/getSavedPostSearchById',
              name: 'getSavedPostSearchById',
            },
          })
        )
    }

    if (filter === 'niches') {
      return this.getNichesByIDs(value)
        .then(filters => filters)
        .catch(e =>
          datadog.dispatchGenericErrorLog({
            error: e,
            log: {
              from: 'utils/filters',
              message: 'Error on filters/getNichesByIDs',
              name: 'getNichesByIDs',
            },
          })
        )
    }

    if (filter === 'tags') {
      return this.getTagsById(value)
        .then(filters => filters)
        .catch(e =>
          datadog.dispatchGenericErrorLog({
            error: e,
            log: {
              from: 'utils/filters',
              message: 'Error on filters/getTagsById',
              name: 'getTagsById',
            },
          })
        )
    }

    if (filter === 'industry_topic') {
      return this.getIndustryTopicsById(value).then(filters => filters)
    }

    if (filter === 'global_topic' || filter in GLOBAL_TAXONOMY_PATHNAMES_BY_TYPE) {
      return this.getTerritoriesByTypeAndIds(filter, value)
    }

    if (this.list?.[group]?.[filter] !== undefined) {
      if (defaultField) {
        return defaultFilterValues[group][filter] || {}
      }

      if (value || value === '') {
        if (Array.isArray(value)) {
          return this.list[group][filter].filter(v => {
            return value.includes(v.value)
          })
        }
        if (value.includes('custom') && filter === 'uploadDate') {
          const uploadDateVideoObject = this.list[group][filter].filter(f => String(f.key) === 'custom')[0]

          return {
            ...uploadDateVideoObject,
            staticValue: value,
          }
        }
        if (!Array.isArray(this.list[group][filter])) {
          return {
            ...this.list[group][filter],
            value: Boolean(value),
          }
        }
        return this.list[group][filter].filter(f => String(f.value) === value)[0]
      } else {
        return this.list[group][filter]
      }
    }
    return false
  }

  getFiltersQueryString(filters = {}, group) {
    const filterValues = Object.keys(filters).reduce((accumulator, key) => {
      if (filters[key]) {
        const isDefaultFilter = this.isDefaultFilter(group, key, filters)

        if (isDefaultFilter) return accumulator

        if (typeof filters[key] === 'string') {
          accumulator[key] = filters[key]
        }

        if (Array.isArray(filters[key])) {
          accumulator[key] = filters[key].map(f => f.value)
        }
        if (filters[key].value) {
          accumulator[key] = filters[key].value
        }
        return accumulator
      }
      return accumulator
    }, {})
    return queryString.stringify(filterValues)
  }

  isDefaultFilter(group, filter, selectedFilters) {
    const defaultFilter = this.getDefaultFilter({
      group,
      filter,
    })
    const selectedFilter = (selectedFilters || this.state.filters)[filter]

    if (defaultFilter) {
      if (
        defaultFilter.value !== undefined &&
        selectedFilter.value !== undefined &&
        defaultFilter.value === selectedFilter.value
      )
        return true
    }

    if (
      Array.isArray(defaultFilter.value) &&
      Array.isArray(selectedFilter.value) &&
      !defaultFilter.value.length &&
      !selectedFilter.value.length
    ) {
      return true
    }

    if (
      Array.isArray(selectedFilter) &&
      selectedFilter.length === 1 &&
      selectedFilter[0].value === defaultFilter.value
    ) {
      return true
    }

    if (Array.isArray(defaultFilter) && Array.isArray(selectedFilter)) {
      return equals(defaultFilter.map(f => f.value).sort(), selectedFilter.map(f => f.value).sort())
    }

    return false
  }
}

export default new Filters()
